import { LineChart, Pentagon, Route, Square, type LucideIcon } from "lucide-react";
import {
  MEASUREMENT_TOOL_IDS,
  MEASUREMENT_TYPES as DOMAIN_MEASUREMENT_TYPES,
  type MeasurementToolId,
  type MeasurementUnit,
} from "@/lib/measurement-domain";
import {
  VIEWPORT_TOOL_KINDS,
  VIEWPORT_TOOLSET_IDS,
  VIEWPORT_TOOLSET_PLACEMENTS,
  type ViewportToolsetDefinition,
} from "@/viewport-tools/core/contracts";

export const TOOL_IDS = MEASUREMENT_TOOL_IDS;

export type ToolType = MeasurementToolId;

export const MEASUREMENT_TYPES = DOMAIN_MEASUREMENT_TYPES;

export type MeasurementType = MeasurementUnit;

export const DEFAULT_MEASUREMENT_TYPE = MEASUREMENT_TYPES.LF;

export interface MeasurementIncrementOption {
  label: string;
  stepFeet: number;
}

export interface MeasurementToolDefinition {
  id: ToolType;
  label: string;
  icon: LucideIcon;
  toolsetId: typeof VIEWPORT_TOOLSET_IDS.measurement;
  kind: typeof VIEWPORT_TOOL_KINDS.draw;
  measurementTypes: MeasurementType[];
}

export type MeasurementToolsetDefinition = ViewportToolsetDefinition<
  typeof VIEWPORT_TOOLSET_IDS.measurement,
  ToolType
>;

export const MEASUREMENT_TOOL_DEFINITIONS: Record<ToolType, MeasurementToolDefinition> = {
  [TOOL_IDS.line]: {
    id: TOOL_IDS.line,
    label: "Line",
    icon: LineChart,
    toolsetId: VIEWPORT_TOOLSET_IDS.measurement,
    kind: VIEWPORT_TOOL_KINDS.draw,
    measurementTypes: [MEASUREMENT_TYPES.LF, MEASUREMENT_TYPES.SQFT],
  },
  [TOOL_IDS.polyline]: {
    id: TOOL_IDS.polyline,
    label: "Polyline",
    icon: Route,
    toolsetId: VIEWPORT_TOOLSET_IDS.measurement,
    kind: VIEWPORT_TOOL_KINDS.draw,
    measurementTypes: [MEASUREMENT_TYPES.LF, MEASUREMENT_TYPES.SQFT],
  },
  [TOOL_IDS.rectangle]: {
    id: TOOL_IDS.rectangle,
    label: "Rectangle",
    icon: Square,
    toolsetId: VIEWPORT_TOOLSET_IDS.measurement,
    kind: VIEWPORT_TOOL_KINDS.draw,
    measurementTypes: [
      MEASUREMENT_TYPES.LF,
      MEASUREMENT_TYPES.SQFT,
      MEASUREMENT_TYPES.CF,
    ],
  },
  [TOOL_IDS.polygon]: {
    id: TOOL_IDS.polygon,
    label: "Polygon",
    icon: Pentagon,
    toolsetId: VIEWPORT_TOOLSET_IDS.measurement,
    kind: VIEWPORT_TOOL_KINDS.draw,
    measurementTypes: [
      MEASUREMENT_TYPES.LF,
      MEASUREMENT_TYPES.SQFT,
      MEASUREMENT_TYPES.CF,
    ],
  },
};

export const MEASUREMENT_TOOLSET: MeasurementToolsetDefinition = {
  id: VIEWPORT_TOOLSET_IDS.measurement,
  label: "Measure",
  placement: VIEWPORT_TOOLSET_PLACEMENTS.leftRail,
  tools: [TOOL_IDS.line, TOOL_IDS.polyline, TOOL_IDS.rectangle, TOOL_IDS.polygon],
};

export const MEASUREMENT_TYPES_BY_TOOL: Record<ToolType, MeasurementType[]> = {
  [TOOL_IDS.line]: MEASUREMENT_TOOL_DEFINITIONS[TOOL_IDS.line].measurementTypes,
  [TOOL_IDS.polyline]: MEASUREMENT_TOOL_DEFINITIONS[TOOL_IDS.polyline].measurementTypes,
  [TOOL_IDS.rectangle]:
    MEASUREMENT_TOOL_DEFINITIONS[TOOL_IDS.rectangle].measurementTypes,
  [TOOL_IDS.polygon]:
    MEASUREMENT_TOOL_DEFINITIONS[TOOL_IDS.polygon].measurementTypes,
};

export const DEFAULT_MEASUREMENT_TYPE_BY_TOOL: Record<ToolType, MeasurementType> = {
  [TOOL_IDS.line]: MEASUREMENT_TYPES.LF,
  [TOOL_IDS.polyline]: MEASUREMENT_TYPES.LF,
  [TOOL_IDS.rectangle]: MEASUREMENT_TYPES.SQFT,
  [TOOL_IDS.polygon]: MEASUREMENT_TYPES.SQFT,
};

export const MEASUREMENT_INCREMENT_OPTIONS: MeasurementIncrementOption[] = [
  { label: `1'-0"`, stepFeet: 1 },
  { label: `6"`, stepFeet: 0.5 },
  { label: `3"`, stepFeet: 0.25 },
  { label: `1"`, stepFeet: 1 / 12 },
];

export function getMeasurementToolDefinition(tool: ToolType): MeasurementToolDefinition {
  return MEASUREMENT_TOOL_DEFINITIONS[tool];
}

export function getMeasurementToolDefinitions(
  toolset: MeasurementToolsetDefinition = MEASUREMENT_TOOLSET
): MeasurementToolDefinition[] {
  return toolset.tools.map((toolId) => MEASUREMENT_TOOL_DEFINITIONS[toolId]);
}

export function getDefaultMeasurementTypeForTool(tool: ToolType): MeasurementType {
  return DEFAULT_MEASUREMENT_TYPE_BY_TOOL[tool];
}
